// Каталог фоновых задач 002 — по specs/002-platform/contracts/jobs.md (003, 004 и 005 — «Фоновая
// задача» в contracts/api.md своих спецификаций): схема данных, предел
// попыток, предел времени попытки и подпись для экрана «Фоновые задачи». Данные задачи — только
// номера записей, числа, даты, булевы и значения перечислений (FR-010): ФИО, почта, тексты
// и фильтры поиска в задачу не попадают (FR-010а). Схемы строгие: лишнее поле отклоняется.
import { z } from '../kernel/zod.ts';
import { IdSchema } from '../kernel/common.ts';

/** Подписчики событий (contracts/jobs.md, «Подписчики событий»); следующие спецификации добавят. */
// 020: письмо «Вам открыли доступ к протоколу» — подписчик канала почты (research, Р9).
export const eventSubscribers = [
  'observability.log',
  'audit.exports',
  'delivery.accessLetter',
  'delivery.recoveryLetter',
  // 030: письмо со ссылкой входа, смена ответственного по связи упоминания, связь учётной записи.
  'delivery.loginLinkLetter',
  'minutes.relink',
  'access.personLinks',
  // 031: уведомления о пунктах по событию ревизии протокола (research Р1, ADR-0046).
  'control.itemNotices',
  // 035: приветственное письмо новому пользователю (contracts/api.md 035).
  'delivery.welcomeLetter',
] as const;
export type EventSubscriber = (typeof eventSubscribers)[number];
export const EventSubscriberSchema = z.enum(eventSubscribers);

export interface JobDefinition {
  /** Подпись типа на экране и в оповещении «Упала задача «…»». */
  readonly label: string;
  readonly data: z.ZodObject;
  /** Попыток до состояния «упала» (FR-005). */
  readonly maxAttempts: number;
  /** Предел времени одной попытки: дольше — попытка прерывается и не удалась (FR-004). */
  readonly timeoutMs: number;
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;

/** Запуск по расписанию: данных нет, ключ идемпотентности — schedule:<имя>:<момент>. */
const scheduled = z.strictObject({});

export const jobCatalog = {
  'recognition.paste_analyze': {
    label: 'Разбор вставляемого фрагмента',
    data: z.strictObject({ analysisId: IdSchema }),
    maxAttempts: 1,
    timeoutMs: 60 * SECOND,
  },
  'recognition.paste_purge': {
    label: 'Очистка временного разбора вставки',
    data: scheduled,
    maxAttempts: 3,
    timeoutMs: 30 * SECOND,
  },
  'access.expire': {
    label: 'Окончание сроков',
    data: scheduled,
    maxAttempts: 3,
    timeoutMs: 30 * SECOND,
  },
  'access.purge': {
    label: 'Чистка попыток входа и сеансов',
    data: scheduled,
    maxAttempts: 3,
    timeoutMs: 2 * MINUTE,
  },
  'kernel.jobs.purge': {
    label: 'Чистка задач, событий и файлов',
    data: scheduled,
    maxAttempts: 3,
    timeoutMs: 2 * MINUTE,
  },
  'kernel.event.deliver': {
    label: 'Доставка события',
    data: z.strictObject({ eventId: IdSchema, subscriber: EventSubscriberSchema }),
    maxAttempts: 10,
    timeoutMs: 30 * SECOND,
  },
  'audit.export': {
    label: 'Выгрузка журнала',
    data: z.strictObject({ exportId: IdSchema }),
    maxAttempts: 3,
    timeoutMs: 10 * MINUTE,
  },
  'audit.exports.purge': {
    label: 'Чистка файлов выгрузки',
    data: scheduled,
    maxAttempts: 3,
    timeoutMs: 2 * MINUTE,
  },
  'observability.log.partitions': {
    label: 'Разделы технического журнала',
    data: scheduled,
    maxAttempts: 5,
    timeoutMs: 2 * MINUTE,
  },
  'observability.alerts.purge': {
    label: 'Чистка истории оповещений',
    data: scheduled,
    maxAttempts: 3,
    timeoutMs: 2 * MINUTE,
  },
  // 003: разбор загруженной версии документа — только номер версии (FR-043).
  'ingestion.parse': {
    label: 'Разбор протокола',
    data: z.strictObject({ versionId: IdSchema }),
    maxAttempts: 3,
    timeoutMs: 2 * MINUTE,
  },
  // 004: свидетельство выполнения после разбора — только номер версии; текста вопроса нет
  // (contracts/api.md 004, «Фоновая задача»; FR-015).
  'ingestion.evidence': {
    label: 'Свидетельство выполнения',
    data: z.strictObject({ versionId: IdSchema }),
    maxAttempts: 3,
    timeoutMs: 10 * MINUTE,
  },
  // 005: связывание упоминаний версии со справочником (contracts/api.md 005, «Фоновая задача»).
  'directory.link': {
    label: 'Связывание упоминаний',
    data: z.strictObject({ versionId: IdSchema }),
    maxAttempts: 3,
    timeoutMs: 2 * MINUTE,
  },
  // 005-3: вопрос ИИ о виде упоминаний версии; null — проход по всем при старте обработчика
  // задач (contracts/api.md 005-3, «Фоновая задача»).
  'directory.kind_ai': {
    label: 'Вид упоминаний: вопрос ИИ',
    data: z.strictObject({ versionId: IdSchema.nullable() }),
    maxAttempts: 3,
    timeoutMs: 10 * MINUTE,
  },
  // 008: канал почты (contracts/api.md 008, «Фоновые задачи»). Данные — только номера.
  'delivery.send': {
    label: 'Отправка письма',
    data: z.strictObject({ messageId: IdSchema }),
    maxAttempts: 10,
    timeoutMs: 30 * SECOND,
  },
  'delivery.connection.check': {
    label: 'Проверка подключения почты',
    data: scheduled,
    maxAttempts: 3,
    timeoutMs: MINUTE,
  },
  'delivery.reports.register': {
    label: 'Регистрация отчётов о доставке',
    data: scheduled,
    maxAttempts: 5,
    timeoutMs: MINUTE,
  },
  'delivery.reply.fetch': {
    label: 'Приём ответного письма',
    data: z.strictObject({ eventId: IdSchema }),
    maxAttempts: 5,
    timeoutMs: MINUTE,
  },
  'delivery.purge': {
    label: 'Чистка писем',
    data: scheduled,
    maxAttempts: 3,
    timeoutMs: 5 * MINUTE,
  },
  // 011: чистка записей команд таблицы протокола старше 30 дней (contracts/api.md 011).
  'minutes.purge': {
    label: 'Чистка записей команд',
    data: scheduled,
    maxAttempts: 3,
    timeoutMs: 5 * MINUTE,
  },
  // 017: разовый перенос границ и объединений из файлов-оригиналов в переведённые протоколы
  // и тонкая рамка протоколов без файла (contracts/api.md 017, «Задача воркера»).
  'minutes.borders_backfill': {
    label: 'Перенос границ ячеек из файлов',
    data: scheduled,
    maxAttempts: 3,
    timeoutMs: 30 * MINUTE,
  },
  // 027: разовый перенос организаций из «Ответственного» в «Контрагент» и связывание текстов
  // «Контрагента» в протоколах в режиме ведения (contracts/api.md 027, «Задача воркера»).
  'minutes.parties_backfill': {
    label: 'Перенос организаций в «Контрагент»',
    data: scheduled,
    maxAttempts: 3,
    timeoutMs: 30 * MINUTE,
  },
  // 030: «Первое письмо» протокола в 08:00 следующего рабочего дня — письма запланированных строк
  // delivery.first_letters (research Р5); ключ first-letters:<протокол>:<время>.
  // 031: поводы по времени — «срок через N рабочих дней» и «просрочено» (FR-008, ADR-0046).
  'control.due_notices': {
    label: 'Напоминания о сроках',
    data: scheduled,
    maxAttempts: 3,
    timeoutMs: 5 * MINUTE,
  },
  // 031: письма о пунктах в ритме планёрки — раз в 15 минут (research Р5).
  'delivery.item_letters': {
    label: 'Письма о пунктах',
    data: scheduled,
    maxAttempts: 3,
    timeoutMs: 5 * MINUTE,
  },
  // 032: копии руководителю — раз в сутки после поводов по сроку (ADR-0047; plan.md 032).
  'answers.manager_copies': {
    label: 'Копии руководителю',
    data: scheduled,
    maxAttempts: 3,
    timeoutMs: 5 * MINUTE,
  },
  'delivery.first_letters.send': {
    label: 'Отправка «Первого письма»',
    data: z.strictObject({ documentId: IdSchema }),
    maxAttempts: 5,
    timeoutMs: 5 * MINUTE,
  },
  // 030: разовое исправление ответственных пунктов по нынешней связи упоминаний (FR-045,
  // research Р7); ставит миграция minutes/0017 с ключом minutes.relink_all:030.
  'minutes.relink_all': {
    label: 'Ответственные по связям упоминаний',
    data: scheduled,
    maxAttempts: 3,
    timeoutMs: 30 * MINUTE,
  },
} as const satisfies Record<string, JobDefinition>;

export type JobType = keyof typeof jobCatalog;

export type JobData<T extends JobType> = z.infer<(typeof jobCatalog)[T]['data']>;

export const jobTypes = Object.keys(jobCatalog) as JobType[];

export const JobTypeSchema = z.enum(jobTypes);

export function isJobType(value: string): value is JobType {
  return Object.hasOwn(jobCatalog, value);
}

/** Задачу этого типа можно ставить по расписанию: данных у неё нет. */
export function isSchedulable(type: JobType): boolean {
  return jobCatalog[type].data.safeParse({}).success;
}

/** Проверка данных перед постановкой: лишние поля (ФИО, почта, фильтры) отклоняются. */
export function parseJobData<T extends JobType>(
  type: T,
  data: unknown,
): z.ZodSafeParseResult<JobData<T>> {
  const schema = jobCatalog[type].data as unknown as z.ZodType<JobData<T>>;
  return schema.safeParse(data);
}
